import { create } from "zustand";
import { notificationsApi, type NotificationDto } from "@api/modules/notifications.api";
import { createSseClient, resolveSseUrl, type SseClient } from "@lib/sse";
import { useAuthStore } from "@stores/auth/authStore";

export interface NotificationState {
    unreadCount: number;
    setUnreadCount: (count: number) => void;
    addRealtimeNotification: (n: NotificationDto) => void;
    markRead: () => void;
    markDeleted: (wasUnread: boolean) => void;
    fetchUnreadCount: () => Promise<void>;
    subscribeRealtime: (listener: (n: NotificationDto) => void) => () => void;
    sseClient: SseClient | null;
    startSse: () => void;
    stopSse: () => void;
}

const realtimeListeners = new Set<(n: NotificationDto) => void>();
const seenNotificationIds = new Set<string>();
const SEEN_ID_LIMIT = 200;

function rememberNotificationId(id: string): boolean {
    if (seenNotificationIds.has(id)) return false;
    seenNotificationIds.add(id);
    if (seenNotificationIds.size > SEEN_ID_LIMIT) {
        const oldest = seenNotificationIds.values().next().value;
        if (oldest) seenNotificationIds.delete(oldest);
    }
    return true;
}

/** The stream sends snake_case; lists send camelCase. Both become one shape. */
function mapRealtimePayload(data: Record<string, unknown>): NotificationDto | null {
    const n = (data.notification ?? data) as Record<string, unknown>;
    if (!n?.id) return null;
    return {
        id: String(n.id),
        title: String(n.title ?? ""),
        message: String(n.message ?? ""),
        type: String(n.type ?? "info"),
        code: n.code != null ? String(n.code) : null,
        data: (n.data as Record<string, unknown>) ?? {},
        url: (n.url as string) ?? null,
        readAt: ((n.readAt ?? n.read_at) as string) ?? null,
        createdAt: ((n.createdAt ?? n.created_at) as string) ?? new Date().toISOString(),
    };
}

export const useNotificationStore = create<NotificationState>((set, get) => ({
    unreadCount: 0,
    setUnreadCount: (count) => set({ unreadCount: Math.max(0, count) }),
    addRealtimeNotification: (n) => {
        if (!rememberNotificationId(n.id)) return;
        set((s) => ({ unreadCount: n.readAt ? s.unreadCount : s.unreadCount + 1 }));
        realtimeListeners.forEach((listener) => listener(n));
    },
    markRead: () => set((s) => ({ unreadCount: Math.max(0, s.unreadCount - 1) })),
    markDeleted: (wasUnread) =>
        set((s) => ({ unreadCount: wasUnread ? Math.max(0, s.unreadCount - 1) : s.unreadCount })),
    fetchUnreadCount: async () => {
        if (useAuthStore.getState().billingOnly) return;
        try {
            const res = await notificationsApi.unreadCount();
            set({ unreadCount: res?.unreadCount ?? 0 });
        } catch {
            /* the count is a nicety; the next poll will retry */
        }
    },
    subscribeRealtime: (listener) => {
        realtimeListeners.add(listener);
        return () => {
            realtimeListeners.delete(listener);
        };
    },
    sseClient: null,
    startSse: () => {
        const { sseClient, stopSse } = get();
        if (sseClient) stopSse();

        const { token, broadcast, billingOnly } = useAuthStore.getState();
        if (!token || billingOnly) return;

        const client = createSseClient({
            url: resolveSseUrl(broadcast?.sse?.url ?? "/tenant/sse"),
            token,
            events: broadcast?.sse?.events ?? {
                connected: "connected",
                notification: "notification",
                heartbeat: "heartbeat",
            },
            onConnected: () => {
                void get().fetchUnreadCount();
            },
            onNotification: (payload) => {
                const dto = mapRealtimePayload(payload);
                if (!dto) return;
                get().addRealtimeNotification(dto);
                void get().fetchUnreadCount();
            },
        });

        set({ sseClient: client });
    },
    stopSse: () => {
        const { sseClient } = get();
        if (sseClient) {
            sseClient.close();
            set({ sseClient: null });
        }
    },
}));
